import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";
import {
  closeModal,
  Modal,
  openModal,
  Select,
  TextInput,
} from "risa-oasis-ui_v2";
import { DrugData } from "../../../data-model/drugData";
import { ModalId } from "../../../enums/modalId";
import CustomRadioButton from "../../customRadioButton/customRadioButton";
import { ToastMessages } from "../../toasts/toastMessages";

interface AddNewDxModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    jCode?: string;
    regimenCode?: string;
    regimenName?: string;
    usePrevious: "yes" | "no";
    selectedId: string;
  }) => void;
  drugOptions?: DrugData[];
  regimenOptions?: Array<{
    id: string;
    regimenCode: string;
    regimenName: string;
  }>;
  mode?: "drug" | "regimen";
  loading?: boolean;
}

const AddNewDxModal: React.FC<AddNewDxModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  drugOptions = [],
  regimenOptions = [],
  mode = "drug",
  loading = false,
}) => {
  const [jCode, setJCode] = useState("");
  const [regimenCode, setRegimenCode] = useState("");
  const [regimenName, setRegimenName] = useState("");
  const [usePrevious, setUsePrevious] = useState<"yes" | "no">("yes");
  const [selectedId, setSelectedId] = useState<string>("");

  // Open or close the modal when isOpen changes
  useEffect(() => {
    if (isOpen) {
      openModal(ModalId.ADD_NEW_DX_MODAL);
    } else {
      closeModal(ModalId.ADD_NEW_DX_MODAL);
    }
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setJCode("");
      setRegimenCode("");
      setRegimenName("");
      setUsePrevious("yes");
      setSelectedId("");
    }
  }, [isOpen]);

  const handleCancel = () => {
    onClose();
  };

  const handleSubmit = () => {
    if (usePrevious === "yes" && !selectedId.trim()) {
      toast.error(ToastMessages.DX_CODE_CONFIG_SELECT_PREVIOUS_REQUIRED);
      return;
    }
    if (mode === "drug") {
      if (!jCode.trim()) {
        toast.error(ToastMessages.DX_CODE_CONFIG_J_CODE_REQUIRED);
        return;
      }
      onSubmit({
        jCode,
        usePrevious,
        selectedId,
      });
    } else {
      if (!regimenCode.trim() || !regimenName.trim()) {
        toast.error(ToastMessages.DX_CODE_CONFIG_REGIMEN_DETAILS_REQUIRED);
        return;
      }
      onSubmit({
        regimenCode,
        regimenName,
        usePrevious,
        selectedId,
      });
    }
  };

  const drugSelectOptions = drugOptions.map((drug) => ({
    label: `${drug.jCode} - ${drug.drugName}`,
    value: drug.id,
  }));

  const regimenSelectOptions = regimenOptions.map((regimen) => ({
    label: `${regimen.regimenCode} - ${regimen.regimenName}`,
    value: regimen.id,
  }));

  return (
    <Modal
      dialogId={ModalId.ADD_NEW_DX_MODAL}
      title={mode === "drug" ? "Add New Drug" : "Add New Regimen"}
      onCancel={handleCancel}
      onSave={handleSubmit}
      cancelText="Cancel"
      saveButtonText={mode === "drug" ? "Add Drug" : "Add Regimen"}
      onClose={handleCancel}
      disableSave={loading}
    >
      <div className="add-new-dx-modal">
        <div className="flex flex-col gap-4 text-tiny font-normal leading-tiny tracking-tightest">
          {mode === "drug" ? (
            <>
              {/* J Code Input */}
              <TextInput
                id="jCode"
                label="J Code"
                placeholder="Enter J code"
                onChange={(data) => setJCode(data.value)}
                defaultValue={jCode}
                required={true}
              />
            </>
          ) : (
            <>
              {/* Regimen Code Input */}
              <TextInput
                id="regimenCode"
                label="Regimen Code"
                placeholder="Enter Regimen Code"
                onChange={(data) => setRegimenCode(data.value)}
                defaultValue={regimenCode}
                required={true}
              />
              {/* Regimen Name Input */}
              <TextInput
                id="regimenName"
                label="Regimen Name"
                placeholder="Enter Regimen Name"
                onChange={(data) => setRegimenName(data.value)}
                defaultValue={regimenName}
                required={true}
              />
            </>
          )}

          {/* Radio Buttons */}
          <div className="flex flex-col gap-2">
            <div className="mb-2 text-h12 font-semiBold leading-small text-primaryGray-1">
              Do you want to use any previous{" "}
              {mode === "drug" ? "drugs" : "regimens"} config
            </div>
            <div className="flex flex-row gap-3">
              <CustomRadioButton
                name="usePrevious"
                value="yes"
                checked={usePrevious === "yes"}
                label="Yes"
                onChange={(e) => {
                  if (e.target.value === "yes") {
                    setUsePrevious("yes");
                  }
                }}
              />
              <CustomRadioButton
                name="usePrevious"
                value="no"
                checked={usePrevious === "no"}
                label="No, build from scratch"
                onChange={(e) => {
                  if (e.target.value === "no") {
                    setUsePrevious("no");
                  }
                }}
              />
            </div>
          </div>

          {/* Selection Dropdown */}
          {usePrevious === "yes" && (
            <Select
              id="selectedId"
              label={`Select the ${mode === "drug" ? "drug" : "regimen"} you want to use`}
              options={
                mode === "drug" ? drugSelectOptions : regimenSelectOptions
              }
              placeholder={`Select ${mode === "drug" ? "J code" : "Regimen"}`}
              onOptionChange={(option) => setSelectedId(option.value)}
              defaultValue={selectedId}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddNewDxModal;
